import Text from "@/components/ui/text";
import React, { memo, useMemo, useState } from "react";
import {
  BarChart as RechartsBarChart,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer,
  Legend,
  Tooltip as RechartsTooltip,
  TooltipProps
} from "recharts";
import {
  NameType,
  ValueType
} from "recharts/types/component/DefaultTooltipContent";
import { cn } from "@/utils/utils";
import { Confetti } from "@dreamhub/designsystem/icons";
import IconContainer from "@/components/ui/icon-container";
import { formatNumber } from "@/utils/numbers";
import { useTranslation } from "react-i18next";
import { simpleDate } from "@/utils/datetime";
import { isEmpty } from "lodash";

export interface MultiColumnChartDataType {
  // monthly variables
  month?: number;
  year?: number;
  // quarterly variables
  quarter?: number;
  startMonth?: number;
  startYear?: number;
  endMonth?: number;
  endYear?: number;
  series: {
    [seriesKey: string]: {
      value: number;
      color?: string;
      pattern?: "solid" | "striped" | "dotted";
      showStar?: boolean;
      label?: string;
      width?: number;
      zIndex?: number;
    };
  };
}

export interface MouseMoveEvent {
  activeCoordinate?: {
    x: number;
    y: number;
  };
  activeTooltipIndex?: number;
  [key: string]: any;
}

interface Props {
  height: number;
  chartData: MultiColumnChartDataType[];
  tooltipYpos?: number;
  isPercentage?: boolean;
  chartClassName?: string;
  legendClassName?: string;
  barWidth?: number;
}

const CustomBar = (props: any) => {
  const { x, y, width, height, fill, pattern = "solid", seriesKey } = props;

  if (height <= 0) {
    return null;
  }

  const effectiveHeight = Math.max(height, 2);
  const adjustedY = y + (height - effectiveHeight);
  const cornerRadius = 3;

  const patternId = `pattern-${pattern}-${Math.random().toString(36).substr(2, 9)}`;
  const gradientId = `gradient-${seriesKey}-${Math.random().toString(36).substr(2, 9)}`;

  const renderPattern = () => {
    switch (pattern) {
      case "striped":
        return (
          <defs>
            <pattern
              id={patternId}
              patternUnits="userSpaceOnUse"
              width="8"
              height="8"
              patternTransform="rotate(-45)"
            >
              <rect width="8" height="8" fill="transparent" />
              <line
                x1="0"
                y1="4"
                x2="8"
                y2="4"
                stroke="#10A47D"
                strokeWidth="2"
              />
            </pattern>
          </defs>
        );
      case "dotted":
        return null;
      default:
        return null;
    }
  };

  const getFillValue = () => {
    if (pattern === "dotted") {
      return "transparent";
    }
    if (pattern === "solid" && seriesKey === "actualSales") {
      return `url(#${gradientId})`;
    }
    return pattern === "solid" ? fill : `url(#${patternId})`;
  };

  const renderGradient = () => {
    if (pattern === "solid" && seriesKey === "actualSales") {
      return (
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#54F5BA" />
            <stop offset="100%" stopColor="#14C993" />
          </linearGradient>
        </defs>
      );
    }
    return null;
  };

  const getStrokeProps = () => {
    if (pattern === "dotted") {
      return {
        stroke: "#667085",
        strokeWidth: 2,
        strokeDasharray: "4,4",
        fill: "transparent"
      };
    }
    if (pattern === "striped") {
      return {
        fill: getFillValue(),
        stroke: "#00F5A3",
        strokeWidth: 1
      };
    }
    return {
      fill: getFillValue()
    };
  };

  return (
    <g>
      {renderPattern()}
      {renderGradient()}
      <path
        d={`
          M ${x},${adjustedY + effectiveHeight}
          L ${x},${adjustedY + cornerRadius}
          Q ${x},${adjustedY} ${x + cornerRadius},${adjustedY}
          L ${x + width - cornerRadius},${adjustedY}
          Q ${x + width},${adjustedY} ${x + width},${adjustedY + cornerRadius}
          L ${x + width},${adjustedY + effectiveHeight}
          Z
        `}
        {...getStrokeProps()}
      />
    </g>
  );
};

const OverlappingBars = (props: any): JSX.Element => {
  const { x, y, width, height, payload } = props;

  const series = payload?.series || {};
  const baseBarWidth = 20;

  const actualSales = series.actualSales;
  const quota = series.quota;
  const shouldShowConfetti =
    actualSales && quota && actualSales.value >= quota.value;

  const getSeriesDefaults = (key: string, dateValue: number | string) => {
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();

    let isPast = false;
    if (typeof dateValue === "string" && dateValue.startsWith("Q")) {
      const quarterNum = parseInt(dateValue.substring(1));
      const currentQuarter = Math.floor(currentMonth / 3) + 1;
      const itemStartYear = payload.startYear || payload.year || currentYear;

      if (itemStartYear < currentYear) {
        isPast = true;
      } else if (itemStartYear === currentYear) {
        isPast = quarterNum <= currentQuarter;
      } else {
        isPast = false;
      }
    } else if (typeof dateValue === "number") {
      const itemYear = payload.year || currentYear;

      if (itemYear < currentYear) {
        isPast = true;
      } else if (itemYear === currentYear) {
        isPast = dateValue <= currentMonth + 1;
      } else {
        isPast = false;
      }
    }

    switch (key) {
      case "quota":
        return {
          width: 1.7,
          zIndex: 1,
          label: "Quota",
          color: "#3A4860",
          pattern: isPast ? "solid" : "dotted"
        };
      case "actualSales":
        return {
          width: 1,
          zIndex: 3,
          label: "Actual Sales",
          color: "#10B981",
          pattern: "solid"
        };
      case "committedDeals":
        return {
          width: 1,
          zIndex: 2,
          label: "Committed Deals",
          color: "#10B981",
          pattern: "striped"
        };
      default:
        return { width: 1, zIndex: 0 };
    }
  };

  const seriesWithDefaults = Object.entries(series).map(
    ([key, seriesData]: [string, any]) => {
      const defaults = getSeriesDefaults(key, payload.date);
      return [
        key,
        {
          ...defaults,
          ...seriesData
        }
      ] as [string, any];
    }
  );

  const sortedSeries = seriesWithDefaults.sort((a, b) => {
    return (a[1].zIndex || 0) - (b[1].zIndex || 0);
  });

  if (sortedSeries.length === 0) {
    return <g />;
  }

  const maxValue = Math.max(...Object.values(series).map((s: any) => s.value));
  const chartHeight = Math.abs(height);

  return (
    <g>
      {sortedSeries.map(([key, seriesData]: [string, any]) => {
        if (seriesData.value <= 0) {
          return null;
        }

        const barWidth = baseBarWidth * (seriesData.width || 1.0);
        const barX = x + width / 2 - barWidth / 2;
        const barHeight = (seriesData.value / maxValue) * chartHeight;
        const barY = y + chartHeight - barHeight;

        return (
          <CustomBar
            key={key}
            x={barX}
            y={barY}
            width={barWidth}
            height={barHeight}
            fill={seriesData.color}
            pattern={seriesData.pattern || "solid"}
            seriesKey={key}
          />
        );
      })}
      {shouldShowConfetti && (
        <foreignObject x={x + width / 2 - 12} y={y - 35} width="24" height="24">
          <IconContainer className="h-full w-full">
            <Confetti />
          </IconContainer>
        </foreignObject>
      )}
    </g>
  );
};

const MultiColumnTooltip = memo(
  ({ active, payload, label }: TooltipProps<ValueType, NameType>) => {
    if (!active || !payload || !payload.length) return null;

    const data = payload[0].payload as any;
    const series = data.series || {};

    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();
    const currentQuarter = Math.floor(currentMonth / 3) + 1;
    const dateValue = data.date;

    let isPast = false;
    let isCurrent = false;
    let isFuture = false;

    if (typeof dateValue === "string" && dateValue.startsWith("Q")) {
      const quarterNum = parseInt(dateValue.substring(1));
      const itemStartYear = data.startYear || data.year || currentYear;

      if (itemStartYear < currentYear) {
        isPast = true;
        isCurrent = false;
        isFuture = false;
      } else if (itemStartYear === currentYear) {
        isPast = quarterNum < currentQuarter;
        isCurrent = quarterNum === currentQuarter;
        isFuture = quarterNum > currentQuarter;
      } else {
        isPast = false;
        isCurrent = false;
        isFuture = true;
      }
    } else if (typeof dateValue === "number") {
      const itemYear = data.year || currentYear;

      if (itemYear < currentYear) {
        isPast = true;
        isCurrent = false;
        isFuture = false;
      } else if (itemYear === currentYear) {
        isPast = dateValue < currentMonth + 1;
        isCurrent = dateValue === currentMonth + 1;
        isFuture = dateValue > currentMonth + 1;
      } else {
        isPast = false;
        isCurrent = false;
        isFuture = true;
      }
    }

    const formatTimePeriod = () => {
      const item = payload[0].payload;

      if (typeof dateValue === "string" && dateValue.startsWith("Q")) {
        const quarterNum = dateValue.substring(1);
        const year = item.startYear || item.year || new Date().getFullYear();
        return `Q${quarterNum} ${year}`;
      } else if (typeof dateValue === "number") {
        const year = item.year || new Date().getFullYear();
        const date = new Date(year, dateValue - 1);
        return date.toLocaleDateString("en-US", {
          month: "long",
          year: "numeric"
        });
      }
      return label;
    };

    const getDisplayItems = () => {
      const items = [];

      const formatValue = (value: number) => {
        const formatted = formatNumber(value, 2, true);
        return `${formatted.currencySign}${formatted.value}${formatted.modifier}`;
      };

      if (isPast) {
        if (series.actualSales) {
          items.push({
            key: "actualSales",
            label: "Actual Sales",
            value: formatValue(series.actualSales.value),
            color: series.actualSales.color
          });
        }
        if (series.quota) {
          items.push({
            key: "quota",
            label: "Quota",
            value: formatValue(series.quota.value),
            color: series.quota.color
          });
        }
        if (series.actualSales && series.quota && series.quota.value > 0) {
          const percentage = Math.round(
            (series.actualSales.value / series.quota.value) * 100
          );
          items.push({
            key: "percentage",
            label: "Quota Achievement",
            value: `${percentage}%`,
            color: "#10B981"
          });
        }
      } else if (isCurrent) {
        if (series.actualSales) {
          items.push({
            key: "actualSales",
            label: "Actual Sales",
            value: formatValue(series.actualSales.value),
            color: series.actualSales.color
          });
        }
        if (series.quota) {
          items.push({
            key: "quota",
            label: "Quota",
            value: formatValue(series.quota.value),
            color: series.quota.color
          });
        }
        if (series.committedDeals) {
          items.push({
            key: "committedDeals",
            label: "In Committed Deals",
            value: formatValue(series.committedDeals.value),
            color: series.committedDeals.color
          });
        }
        if (series.actualSales && series.quota && series.quota.value > 0) {
          const percentage = Math.round(
            (series.actualSales.value / series.quota.value) * 100
          );
          items.push({
            key: "percentage",
            label: "Quota Achievement",
            value: `${percentage}%`,
            color: "#10B981"
          });
        }
      } else if (isFuture) {
        if (series.quota) {
          items.push({
            key: "quota",
            label: "Quota",
            value: formatValue(series.quota.value),
            color: series.quota.color
          });
        }
        if (series.committedDeals) {
          items.push({
            key: "committedDeals",
            label: "In Committed Deals",
            value: formatValue(series.committedDeals.value),
            color: series.committedDeals.color
          });
        }
      }

      return items;
    };

    const displayItems = getDisplayItems();

    return (
      <div className="relative z-50 max-w-250 overflow-hidden rounded-md border-0 bg-surface-tooltip-toast px-3 py-2 shadow-md">
        <div className="w-full flex flex-col">
          <div className="mb-2 text-secondary text-sm">
            {formatTimePeriod()}
          </div>
          <div className="flex flex-col gap-2">
            {displayItems.map((item) => (
              <div
                key={item.key}
                className="flex items-center justify-between gap-2"
              >
                <Text className="text-sm">{item.value}</Text>
                <Text className="text-tertiary text-sm">{item.label}</Text>
              </div>
            ))}
          </div>
        </div>
        <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-0 h-0 border-l-[10px] border-l-transparent border-r-[10px] border-r-transparent border-t-[10px] border-t-surface-tooltip-toast"></div>
      </div>
    );
  }
);

const MultiColumnBarChart: React.FC<Props> = ({
  height,
  chartData,
  tooltipYpos = 10,
  isPercentage = true,
  chartClassName,
  legendClassName,
  barWidth = 12
}) => {
  const { t } = useTranslation();
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });

  if (isEmpty(chartData)) return null;

  const handleMouseMove = (e: MouseMoveEvent) => {
    if (e && e.activeTooltipIndex !== undefined && e.activeCoordinate) {
      setTooltipPos({
        x: e.activeCoordinate.x - 80,
        y: tooltipYpos
      });
    }
  };

  const transformedData = useMemo(() => {
    return chartData.map((item) => {
      const dateValue = item.quarter ? `Q${item.quarter}` : item.month;
      const transformed: any = {
        date: dateValue,
        month: item.month,
        year: item.year,
        quarter: item.quarter,
        startMonth: item.startMonth,
        startYear: item.startYear,
        endMonth: item.endMonth,
        endYear: item.endYear
      };
      Object.entries(item.series).forEach(([key, seriesData]) => {
        transformed[key] = seriesData.value;
      });
      transformed.series = item.series;
      return transformed;
    });
  }, [chartData]);

  const seriesKeys = useMemo(() => {
    const keys = new Set<string>();
    chartData.forEach((item) => {
      Object.keys(item.series).forEach((key) => keys.add(key));
    });

    const keysArray = Array.from(keys);
    return keysArray.sort((a, b) => {
      const aZIndex = chartData[0]?.series[a]?.zIndex || 0;
      const bZIndex = chartData[0]?.series[b]?.zIndex || 0;
      return aZIndex - bZIndex;
    });
  }, [chartData]);

  const getChartTitle = () => {
    if (chartData.length === 0) return "";

    const firstItem = chartData[0];
    const lastItem = chartData[chartData.length - 1];

    const getDateInfo = () => {
      // quarterly data
      if (
        firstItem?.startMonth &&
        firstItem?.startYear &&
        lastItem?.endMonth &&
        lastItem?.endYear
      ) {
        return {
          type: "quarterly" as const,
          startMonth: firstItem.startMonth,
          startYear: firstItem.startYear,
          endMonth: lastItem.endMonth,
          endYear: lastItem.endYear
        };
      }

      // monthly data
      if (
        firstItem?.month &&
        firstItem?.year &&
        lastItem?.month &&
        lastItem?.year
      ) {
        return {
          type: "monthly" as const,
          startMonth: firstItem.month,
          startYear: firstItem.year,
          endMonth: lastItem.month,
          endYear: lastItem.year
        };
      }

      return null;
    };

    const dateInfo = getDateInfo();
    if (!dateInfo) return "";

    // If start month is January (1), return just the year
    if (dateInfo.startMonth === 1) {
      return dateInfo.startYear.toString();
    }

    const yearRange =
      dateInfo.startYear === dateInfo.endYear
        ? dateInfo.startYear.toString()
        : `${dateInfo.startYear} - ${dateInfo.endYear}`;

    const startDate = new Date(dateInfo.startYear, dateInfo.startMonth - 1);
    const endDate = new Date(dateInfo.endYear, dateInfo.endMonth - 1);

    const dateRange = `${simpleDate(startDate, "short", false, true, true)} - ${simpleDate(endDate, "short", false, true, true)}`;

    return `${t("salesVSQuota.fiscalYear", { year: yearRange })} (${dateRange})`;
  };

  return (
    <div className="flex w-full relative" style={{ height: `${height}px` }}>
      <ResponsiveContainer width="100%" height={height}>
        <RechartsBarChart
          data={transformedData}
          margin={{ left: 25, right: 25, top: 25, bottom: 5 }}
          onMouseMove={handleMouseMove}
          barGap={-100}
          barCategoryGap="0%"
          className={chartClassName}
        >
          <defs>
            <linearGradient id="series-gradient-1" x1="0" y1="0" x2="0" y2="1">
              <stop offset="30%" stopColor="#10B981" stopOpacity={0.8} />
              <stop offset="95%" stopColor="#10B981" stopOpacity={0.5} />
            </linearGradient>
            <linearGradient id="series-gradient-2" x1="0" y1="0" x2="0" y2="1">
              <stop offset="30%" stopColor="#3B82F6" stopOpacity={0.8} />
              <stop offset="95%" stopColor="#3B82F6" stopOpacity={0.5} />
            </linearGradient>
            <linearGradient id="series-gradient-3" x1="0" y1="0" x2="0" y2="1">
              <stop offset="30%" stopColor="#8B5CF6" stopOpacity={0.8} />
              <stop offset="95%" stopColor="#8B5CF6" stopOpacity={0.5} />
            </linearGradient>
          </defs>

          <XAxis
            dataKey="date"
            interval={0}
            tickLine={false}
            axisLine={{ stroke: "var(--widget)" }}
            tick={(props) => {
              const { x, y, payload } = props;
              const value = payload.value;

              const currentMonth = new Date().getMonth();
              const currentQuarter = Math.floor(currentMonth / 3) + 1;

              let isCurrent = false;
              let displayValue = value;

              if (typeof value === "string" && value.startsWith("Q")) {
                const quarterNum = parseInt(value.substring(1));
                isCurrent = quarterNum === currentQuarter;
                displayValue = value;
              } else if (typeof value === "number") {
                isCurrent = value === currentMonth + 1;
                const date = new Date(2000, value - 1);
                displayValue = date.toLocaleDateString("en-US", {
                  month: "short"
                });
              }

              return (
                <g>
                  <text
                    x={x}
                    y={y + 8}
                    textAnchor="middle"
                    fill={isCurrent ? "var(--primary)" : "var(--secondary)"}
                    fontSize={12}
                    fontWeight={isCurrent ? "bold" : "normal"}
                  >
                    {displayValue}
                  </text>
                  {isCurrent && (
                    <circle cx={x} cy={y + 20} r={2} fill="var(--primary)" />
                  )}
                </g>
              );
            }}
          />
          <YAxis
            tickLine={false}
            axisLine={{ stroke: "var(--widget)" }}
            tick={{ fill: "var(--secondary)", fontSize: 12 }}
            tickFormatter={(tick) => {
              if (isPercentage) {
                return `${tick}%`;
              }
              const formatted = formatNumber(tick, 2, true);
              return `${formatted.currencySign}${formatted.value}${formatted.modifier}`;
            }}
          />

          <Bar
            dataKey={seriesKeys[0]}
            shape={OverlappingBars}
            barSize={barWidth * 2}
          />

          <RechartsTooltip
            content={<MultiColumnTooltip />}
            cursor={{ fill: "transparent" }}
            position={tooltipPos}
            wrapperStyle={{ visibility: tooltipPos.x ? "visible" : "hidden" }}
          />
          <Legend
            content={() => (
              <div
                className={cn(
                  "absolute -bottom-3 left-1/2 w-full",
                  legendClassName
                )}
              >
                <Text as="div" variant="smBodySecondary">
                  {getChartTitle()}
                </Text>
              </div>
            )}
            verticalAlign="bottom"
            align="center"
          />
        </RechartsBarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default MultiColumnBarChart;
